'use client';

import type { CSSProperties, HTMLAttributes } from 'react';
import { cx } from '../utils/cx';
import { resolveSpaceCssLength } from '../utils/spacing';

export type FlexAlign = 'start' | 'center' | 'end' | 'baseline' | 'stretch';
export type FlexJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
export type InlinePreset = 'default' | 'bottomNavRail';

const FLEX_ALIGN_CLASS: Record<FlexAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  baseline: 'items-baseline',
  stretch: 'items-stretch',
};

const FLEX_JUSTIFY_CLASS: Record<FlexJustify, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
};

export interface InlineProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Gap between children:
   * - token: "xs" | "sm" | "md" | "lg" | "xl" | "2xl"
   * - number: spacing units (multiplied by `--spacing`)
   * - css length: "12px", "1rem", "clamp(...)"
   */
  gap?: number | string;
  preset?: InlinePreset;
  align?: FlexAlign;
  justify?: FlexJustify;
  wrap?: boolean;
  grow?: boolean;
  fullWidth?: boolean;
}

const resolveGapStyle = (gap: number | string | undefined): CSSProperties => {
  const css = resolveSpaceCssLength(gap, 2);
  if (!css) return {};
  return { gap: css };
};

function presetClass(preset: InlinePreset): string {
  switch (preset) {
    case 'bottomNavRail':
      return 'overflow-hidden rounded-[1.35rem]';
    case 'default':
    default:
      return '';
  }
}

export function Inline({
  gap = 'sm',
  preset = 'default',
  align = 'center',
  justify = 'start',
  wrap = false,
  grow = false,
  fullWidth = false,
  className,
  style,
  children,
  ...rest
}: InlineProps) {
  return (
    <div
      {...rest}
      style={{ ...resolveGapStyle(gap), ...style }}
      className={cx(
        'flex flex-row',
        FLEX_ALIGN_CLASS[align],
        FLEX_JUSTIFY_CLASS[justify],
        presetClass(preset),
        wrap ? 'flex-wrap' : 'flex-nowrap',
        grow && 'flex-1 min-w-0',
        fullWidth && 'w-full',
        className,
      )}
    >
      {children}
    </div>
  );
}

Inline.displayName = 'Inline';
